import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Phone, MessageCircle, ExternalLink, Trophy, RefreshCw, TrendingDown, ChevronDown } from 'lucide-react';
import api from '../services/api';
import GlobalNavigationMenu from '../components/GlobalNavigationMenu';
import { buildHandoverText, waLinkWithText } from '../utils/parkHandoverText';

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const todayLabel = () =>
  new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });

// reason badge colour
const reasonBadge = (r = '') => {
  if (r.includes('overdue'))               return 'bg-red-100 text-red-700';
  if (r.includes('today'))                 return 'bg-amber-100 text-amber-700';
  if (r.includes('Occasion') || r.includes('d)') || r.includes(' in ')) return 'bg-purple-100 text-purple-700';
  if (r.includes('Parked'))                return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-700';
};

// ── component ─────────────────────────────────────────────────────────────────
const SalesmanDashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [followupsOpen, setFollowupsOpen] = useState(true);

  const fetchData = () => {
    setLoading(true);
    api.get('/crm/dashboard/salesman')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const name = data?.salesman_name || currentUser?.username || '';

  return (
    <div className="h-screen flex flex-col bg-gray-50">

      {/* ── Dark header ── */}
      <div className="bg-[#075E54] text-white flex-shrink-0">
        {/* Nav row */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-1">
          <span className="text-base font-semibold flex-1">My Dashboard</span>
          {!loading && (
            <button onClick={fetchData} className="text-emerald-300 hover:text-white p-1" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
          {/* Navigation menu — universal GlobalNavigationMenu */}
          <GlobalNavigationMenu />
        </div>

        {/* Greeting + clickable action chips */}
        <div className="px-5 pb-4">
          <p className="text-xs text-emerald-300">{todayLabel()}</p>
          <h2 className="text-xl font-bold mt-0.5">{greeting()}, {name}</h2>

          {/* Clickable action chips */}
          {data && (
            <div className="flex items-center gap-3 mt-3">
              <ActionChip
                value={data.stats.follow_up_count}
                label="Follow-ups"
                urgent={data.stats.follow_up_count > 0}
                onClick={() => navigate('/lead-sessions?lead_status=parked')}
                testId="chip-followups"
              />
              <div className="w-px h-4 bg-emerald-600" />
              <ActionChip
                value={data.stats.pending_estimates}
                label="Estimates"
                onClick={() => navigate('/estimates?status=draft')}
                testId="chip-estimates"
              />
              <div className="w-px h-4 bg-emerald-600" />
              <ActionChip
                value={(data.monthly.open ?? 0) + (data.monthly.parked ?? 0)}
                label="Active Leads"
                onClick={() => navigate('/lead-sessions')}
                testId="chip-open-leads"
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="px-4 py-4 space-y-5 max-w-xl mx-auto">

            {/* ══ FOLLOW UP TODAY — COLLAPSIBLE ══════════════════════════ */}
            <section data-testid="followup-section">
              {/* Full-width tappable header */}
              <button
                className="w-full flex items-center justify-between bg-white rounded-xl border border-gray-100 px-4 py-3 mb-2"
                onClick={() => setFollowupsOpen(o => !o)}
                data-testid="followup-toggle"
              >
                <div className="flex items-center gap-2">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest">Follow Up Today</p>
                  {data.follow_ups.length > 0 && (
                    <span className="text-[11px] font-bold text-white bg-amber-500 rounded-full px-2 py-0.5 leading-none">
                      {data.stats.follow_up_count}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {data.follow_ups.length > 0 && followupsOpen && (
                    <span
                      className="text-xs text-emerald-600 font-semibold"
                      onClick={e => { e.stopPropagation(); navigate('/lead-sessions?lead_status=parked'); }}
                      data-testid="view-all-followups-btn"
                    >
                      View All
                    </span>
                  )}
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${followupsOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {/* Collapsible body */}
              {followupsOpen && (
                data.follow_ups.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
                    <p className="text-3xl mb-2">✓</p>
                    <p className="text-sm font-semibold text-gray-700">All caught up!</p>
                    <p className="text-xs text-gray-400 mt-1">No urgent follow-ups right now</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {data.follow_ups.map((f, i) => (
                      <FollowUpCard key={`${f.session_id}-${i}`} item={f} navigate={navigate} index={i} />
                    ))}
                  </div>
                )
              )}
            </section>

            {/* ══ PIPELINE SNAPSHOT ═══════════════════════════════════════ */}
            <section data-testid="monthly-section">
              {/* This Month */}
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">This Month</p>
              <div className="bg-white rounded-xl border border-gray-100 p-4 mb-3">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-400">Revenue Closed</p>
                    <p className="text-3xl font-bold text-gray-900 leading-none mt-0.5" data-testid="monthly-revenue">
                      {fmt(data.monthly.revenue_closed)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Conversion</p>
                    <p className={`text-2xl font-bold ${data.monthly.conversion_rate >= 50 ? 'text-green-600' : 'text-orange-500'}`}>
                      {data.monthly.conversion_rate}%
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => navigate('/lead-sessions?lead_status=won')}
                    className="text-center py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-100 transition-colors"
                    data-testid="pipeline-won"
                  >
                    {data.monthly.won} Won this month
                  </button>
                  <button
                    onClick={() => navigate('/lead-sessions?lead_status=lost')}
                    className="text-center py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors"
                    data-testid="pipeline-lost"
                  >
                    {data.monthly.lost} Lost this month
                  </button>
                </div>
              </div>

              {/* Current Pipeline */}
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Current Pipeline</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => navigate('/lead-sessions?lead_status=open')}
                  className="bg-white rounded-xl border border-blue-100 p-3 text-left hover:bg-blue-50 transition-colors"
                  data-testid="pipeline-open"
                >
                  <p className="text-2xl font-bold text-blue-700">{data.monthly.open ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Open Leads</p>
                </button>
                <button
                  onClick={() => navigate('/lead-sessions?lead_status=parked')}
                  className="bg-white rounded-xl border border-amber-100 p-3 text-left hover:bg-amber-50 transition-colors"
                  data-testid="pipeline-parked"
                >
                  <p className="text-2xl font-bold text-amber-600">{data.monthly.parked ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Parked Leads</p>
                </button>
                <button
                  onClick={() => navigate('/lead-sessions?status=open')}
                  className="bg-white rounded-xl border border-emerald-100 p-3 text-left hover:bg-emerald-50 transition-colors"
                  data-testid="pipeline-sessions"
                >
                  <p className="text-2xl font-bold text-emerald-600">{data.monthly.open_sessions ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Sessions</p>
                </button>
              </div>

              {/* Parked-by-action breakdown chips — visible only when the salesman
                  actually has parked leads. Each chip navigates to the sessions
                  list already filtered by that park action. */}
              {(data.monthly.parked_by_action || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="parked-by-action-strip">
                  {data.monthly.parked_by_action.map((pa) => (
                    <button
                      key={pa.key}
                      onClick={() => navigate(
                        pa.key === '_other'
                          ? '/lead-sessions?lead_status=parked'
                          : `/lead-sessions?lead_status=parked&park=${pa.key}`
                      )}
                      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 hover:bg-amber-100 transition-colors"
                      data-testid={`parked-chip-${pa.key}`}
                    >
                      {pa.label}
                      <span className="rounded-full bg-white border border-amber-200 px-1.5 text-[10px] font-black text-amber-700">
                        {pa.count}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* ══ LOSS REASON INTELLIGENCE ══════════════════════════════ */}
            <section data-testid="loss-reasons-section">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Why We Lost This Month</p>
                {data.loss_reasons?.length > 0 && (
                  <button
                    onClick={() => navigate('/loss-reason-analytics')}
                    className="text-[10px] text-emerald-600 font-semibold hover:underline"
                    data-testid="view-all-losses-btn"
                  >
                    View All
                  </button>
                )}
              </div>
              <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
                {data.loss_reasons?.length > 0 ? (
                  <div className="space-y-2">
                    {data.loss_reasons.map((lr, i) => {
                      const maxCount = data.loss_reasons[0]?.count || 1;
                      const barWidth = Math.round((lr.count / maxCount) * 100);
                      return (
                        <div key={i} className="space-y-1" data-testid={`loss-reason-${i}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-700 font-medium truncate flex-1 mr-2">{lr.reason}</span>
                            <span className="text-xs font-bold text-red-600 flex-shrink-0">{lr.count}x</span>
                          </div>
                          <div className="w-full bg-red-50 rounded-full h-1.5">
                            <div
                              className="bg-red-400 h-1.5 rounded-full transition-all"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 py-1">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <TrendingDown className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">No losses this month!</p>
                      <p className="text-xs text-gray-400 mt-0.5">Keep up the great work</p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ══ PERSONAL LEADERBOARD ════════════════════════════════════ */}
            <section data-testid="leaderboard-section">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Top Closers This Month</p>
                {data.my_rank && (
                  <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">
                    Your rank: #{data.my_rank}
                  </span>
                )}
              </div>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {data.leaderboard?.length > 0 ? (
                  <div className="divide-y divide-gray-50">
                    {data.leaderboard.map((entry, i) => {
                      const isMe = entry.name === name;
                      return (
                        <div key={i} className={`flex items-center gap-3 px-4 py-3 ${isMe ? 'bg-emerald-50' : ''}`} data-testid={`leaderboard-entry-${i}`}>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0
                            ${i === 0 ? 'bg-yellow-100 text-yellow-700' : i === 1 ? 'bg-gray-100 text-gray-600' : 'bg-orange-50 text-orange-500'}`}>
                            {i + 1}
                          </span>
                          <span className={`flex-1 text-sm ${isMe ? 'font-bold text-emerald-700' : 'text-gray-700'}`}>
                            {entry.name}{isMe && ' (you)'}
                          </span>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-800">{fmt(entry.revenue)}</p>
                            <p className="text-[10px] text-gray-400">{entry.won} won</p>
                          </div>
                          {i === 0 && <Trophy className="w-4 h-4 text-yellow-500 flex-shrink-0" />}
                        </div>
                      );
                    })}
                    {data.my_rank && data.my_rank > data.leaderboard.length && (
                      <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border-t border-emerald-100">
                        <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          #{data.my_rank}
                        </span>
                        <span className="flex-1 text-sm font-bold text-emerald-700">{name} (you)</span>
                        <p className="text-xs text-gray-500">Keep closing!</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 px-4 py-4">
                    <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">No closes yet this month</p>
                      <p className="text-xs text-gray-400 mt-0.5">Be the first to close a deal!</p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <div className="h-4" />
          </div>
        )}
      </div>
      {/* Change Password modal lives in GlobalNavigationMenu */}
    </div>
  );
};

// ── Follow-up card ────────────────────────────────────────────────────────────
const FollowUpCard = ({ item: f, navigate, index }) => (
  <div
    className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
    data-testid={`followup-item-${index}`}
  >
    {/* Main info row */}
    <div className="px-4 pt-3.5 pb-2 flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-emerald-100 flex-shrink-0 flex items-center justify-center mt-0.5">
        <span className="text-emerald-700 font-bold text-sm">{(f.client_name || '?').charAt(0).toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900 text-sm">{f.client_name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${reasonBadge(f.reason)}`}>
            {f.reason}
          </span>
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5">
          {f.product_name || '—'}
          {f.estimated_value > 0 && (
            <span className="text-emerald-600 font-semibold ml-1.5">· {fmt(f.estimated_value)}</span>
          )}
        </p>
      </div>
    </div>

    {/* Action buttons */}
    <div className="flex border-t border-gray-100 divide-x divide-gray-100">
      <a
        href={`tel:${f.client_phone}`}
        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
        onClick={e => e.stopPropagation()}
        data-testid={`call-btn-${index}`}
      >
        <Phone className="w-3.5 h-3.5" />
        Call
      </a>
      <a
        href={waLinkWithText(f.client_phone, f.lead_status === 'parked' ? buildHandoverText({
          client_name: f.client_name,
          product_name: f.product_name,
          park_action: f.park_action,
        }) : undefined)}
        target="_blank"
        rel="noreferrer"
        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors"
        onClick={e => e.stopPropagation()}
        data-testid={`whatsapp-btn-${index}`}
      >
        <MessageCircle className="w-3.5 h-3.5" />
        WhatsApp
      </a>
      <button
        onClick={() => navigate(`/lead-sessions/${f.session_id}`)}
        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
        data-testid={`open-session-btn-${index}`}
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Open
      </button>
    </div>
  </div>
);

// ── Clickable action chip ─────────────────────────────────────────────────────
const ActionChip = ({ value, label, urgent, onClick, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 group cursor-pointer`}
    data-testid={testId}
  >
    <span className={`text-lg font-bold leading-none group-hover:underline ${urgent ? 'text-amber-300' : 'text-white'}`}>{value}</span>
    <span className="text-[11px] text-emerald-300 leading-tight group-hover:text-emerald-200">{label}</span>
  </button>
);

export default SalesmanDashboard;
